import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalContent = 'privacy' | 'terms' | null;
function PolicyModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-53">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-hidden="true" data-id="element-54" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 12,
      scale: 0.97
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-midnight-900 border border-midnight-700/60 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" role="dialog" aria-modal="true" aria-label={type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'} data-id="element-55">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-midnight-700/60" data-id="element-56">
          <h2 className="font-heading text-xl font-bold text-white" data-id="element-57">
            {type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 transition-colors hover:text-white hover:bg-midnight-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rescue-gold/50" aria-label="Close" data-id="element-58">
            <XIcon className="w-5 h-5" data-id="element-59" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto max-h-[calc(80vh-64px)] font-body text-sm text-gray-400 leading-relaxed space-y-4" data-id="element-60">
          {type === 'privacy' ? <PrivacyContent data-id="element-61" /> : <TermsContent data-id="element-62" />}
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-gray-300 font-semibold" data-id="element-63">Last updated: April 2025</p>
      <p data-id="element-64">
        Your privacy matters. This policy explains what information we collect,
        how we use it, and your rights regarding your data when you purchase or
        use The $0 Tax Rescue Guide.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-65">Information We Collect</h3>
      <p data-id="element-66">
        When you purchase the guide, we collect only the information necessary
        to process your transaction: your email address and payment details.
        Payment processing is handled by our third-party payment processor — we
        never store your credit card information directly.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-67">
        How We Use Your Information
      </h3>
      <p data-id="element-68">
        We use your email address solely to deliver your purchase and send
        transactional communications (e.g., order confirmation, download links).
        We do not sell, rent, or share your personal information with third
        parties for marketing purposes.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-69">Cookies &amp; Tracking</h3>
      <p data-id="element-70">
        This site may use minimal analytics cookies to understand page
        performance. No personally identifiable information is collected through
        cookies. You may disable cookies in your browser settings at any time.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-71">Data Retention</h3>
      <p data-id="element-72">
        We retain your email address for the purpose of providing access to your
        purchase. You may request deletion of your data at any time.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-73">Your Rights</h3>
      <p data-id="element-74">
        You have the right to access, correct, or delete your personal data. You
        may also opt out of any non-transactional communications at any time.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-gray-300 font-semibold" data-id="element-75">Last updated: April 2025</p>
      <p data-id="element-76">
        By purchasing and using The $0 Tax Rescue Guide, you agree to the
        following terms and conditions.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-77">Product Description</h3>
      <p data-id="element-78">
        The $0 Tax Rescue Guide is a digital informational product (PDF) that
        provides guidance on using free, publicly available IRS filing tools. It
        is not tax advice, legal advice, or a substitute for professional
        consultation with a licensed tax preparer or CPA.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-79">No Professional Advice</h3>
      <p data-id="element-80">
        The information provided in this guide is for educational and
        informational purposes only. We are not licensed tax professionals,
        CPAs, or attorneys. You are solely responsible for your tax filings and
        any decisions made based on the information in this guide.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-81">Digital Delivery</h3>
      <p data-id="element-82">
        This is a digital product delivered instantly upon purchase. Due to the
        nature of digital goods, all sales are final. No refunds will be issued
        once the product has been delivered.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-83">Intellectual Property</h3>
      <p data-id="element-84">
        All content in The $0 Tax Rescue Guide is protected by copyright. You
        may not reproduce, distribute, or resell any part of this guide without
        written permission.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-85">Limitation of Liability</h3>
      <p data-id="element-86">
        We are not liable for any errors in your tax filing, penalties, or any
        other consequences resulting from the use of this guide. You use this
        product at your own risk and discretion.
      </p>
      <h3 className="text-white font-semibold mt-4" data-id="element-87">Changes to Terms</h3>
      <p data-id="element-88">
        We reserve the right to update these terms at any time. Continued use of
        the product constitutes acceptance of any changes.
      </p>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalContent>(null);
  return <>
      <footer className="px-4 py-8 border-t border-midnight-800" data-id="element-89">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-6" data-id="element-90">
          <button onClick={() => setActiveModal('privacy')} className="font-body text-sm text-gray-500 transition-colors hover:text-rescue-gold focus:outline-none focus-visible:text-rescue-gold" data-id="element-91">
            Privacy Policy
          </button>
          <span className="text-midnight-700" aria-hidden="true" data-id="element-92">
            ·
          </span>
          <button onClick={() => setActiveModal('terms')} className="font-body text-sm text-gray-500 transition-colors hover:text-rescue-gold focus:outline-none focus-visible:text-rescue-gold" data-id="element-93">
            Terms of Service
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-94">
        {activeModal && <PolicyModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-95" />}
      </AnimatePresence>
    </>;
}